import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { usePanelContext } from './PanelContext';
import fireContainedUrl from '../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../assets/icons/fire-almost-gray.png';
import moment from 'moment-timezone';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

const FiresContext = createContext();

export const FiresContextProvider = ({ children }) => {
  const { setPanel } = usePanelContext();
  const [fires, setFires] = useState(null);
  const [modal, setModal] = useState({ type: false, content: null });
  const eventListenersAdded = useRef(false);

  const iconSpecs = [
    ['fire-icon-contained', fireContainedUrl],
    ['fire-icon-half-contained', fireHalfContainedUrl],
    ['fire-circle', fireCircleUrl],
    ['fire-almost-gray', fireAlmostGray],
  ];

  // Memoized WKT-to-rings converter for MultiPolygon WKT
  const WKTtoRings = useCallback((wkt) => {
    const match = wkt.match(/^MULTIPOLYGON\s*\(\(\(\s*([\s\S]+?)\s*\)\)\)$/i);
    if (!match) {
      throw new Error('Invalid MULTIPOLYGON WKT: ' + wkt);
    }
    const coordsText = match[1];
    // Split into individual polygons
    const polygonStrings = coordsText.split(/\)\),\s*\(\(/);
    // For each polygon, split into its rings and parse coordinates
    return polygonStrings.map(polyStr => {
      const ringStrings = polyStr.split(/\)\s*,\s*\(/);
      return ringStrings.map(ringStr =>
        ringStr.trim().split(/\s*,\s*/).map(pair => {
          const [lon, lat] = pair.trim().split(/\s+/).map(Number);
          return [lon, lat];
        })
      );
    });
  }, []);

  // Memoized format timestamp for popups
  const formatDate = useCallback((timestamp) => {
    const time = moment.utc(timestamp).local();
    return time.format('YYYY-MM-DD hh:mm:ss A');
  }, []);

  // Helper function to get fire icon based on containment percentage
  const getFireIcon = useCallback((perContained) => {
    const pc = perContained ?? 0;
    
    if (pc < 41)          return fireCircleUrl;            // 0–40
    if (pc < 91)          return fireHalfContainedUrl;     // 41–90
    if (pc < 100)         return fireAlmostGray;           // 91–99
    /* pc === 100 */      return fireContainedUrl;         // 100
  }, []);

  // Memoized get GeoJSON centroid using Turf.js
  const getCentroid = useCallback((geometry) => {
    const pt = turf.centroid(geometry);
    return pt.geometry.coordinates;
  }, []);

  const plotFires = useCallback((map, fires) => {
    if (!map?.current) {
      console.warn('plotFires: Map not initialized, skipping plot');
      return;
    }
    
    // Check if map style is loaded (required for getStyle() to work)
    // Only check if we're not already in a style.load handler
    if (!map.current.isStyleLoaded() && !map.current._inStyleLoadHandler) {
      console.warn('plotFires: Map style not loaded yet, waiting...');
      // Wait for style to load
      map.current.once('style.load', () => {
        plotFires(map, fires);
      });
      return;
    }
    
    try {
      console.log('plotFires called with:', fires.length, 'fires');
      console.log('Map loaded:', map.current.loaded());
      console.log('Style loaded:', map.current.isStyleLoaded());
      
      // Cleanup old layers and sources
      const symbolLayers = map.current.getStyle().layers?.filter(l => l.id.includes('Layer-Fire-symbol')) || [];
      symbolLayers.forEach(l => { if (map.current.getLayer(l.id)) map.current.removeLayer(l.id); });
      if (map.current.getSource('Source-Fire')) map.current.removeSource('Source-Fire');

      // Load fire icons if not already
      iconSpecs.forEach(([name, url]) => {
        if (!map.current.hasImage(name)) {
          map.current.loadImage(url, (err, img) => {
            if (err) throw err;
            map.current.addImage(name, img);
          });
        }
      });
      
      const firePoints = fires.map(f => {
        // Always use lat/long for initial plotting
        const coords = [f.longitude, f.latitude];
        return turf.point(coords, f);
      });

      const fireCollection = turf.featureCollection(firePoints);
      if (map.current.getSource('Source-Fire')) {
        map.current.getSource('Source-Fire').setData(fireCollection);
      } else {
        map.current.addSource('Source-Fire', { type: 'geojson', data: fireCollection });
      }

      map.current.addLayer({
        id: 'Layer-Fire-symbol',
        type: 'symbol',
        source: 'Source-Fire',
        layout: {
          'icon-image': [
            'step', ['coalesce', ['get', 'perContained'], 0],
            'fire-circle', 41, 'fire-icon-half-contained',
            91, 'fire-almost-gray', 100, 'fire-icon-contained'
          ],
          'icon-size': 0.4,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });

      // Only add event listeners once
      if (!eventListenersAdded.current) {
        map.current.on('click', 'Layer-Fire-symbol', e => {
          const feat = e.features?.[0];
          if (!feat) return;
          const props = feat.properties;
          // Always use lat/long for popup location initially
          const loc = [props.longitude, props.latitude];

          const popup = new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
            .setLngLat(loc)
            .setHTML(`
              <div>
                <div class='alert-popup-title' style="display: flex; align-items: center; gap: 4px;">
                  <img src="${getFireIcon(props.perContained)}" style="width: 23px; height: 23px;" alt="Fire status" />
                  ${props.name}
                </div>
                <div class='alert-popup-row'><div class='alert-popup-row-name'>Status:</div><div class='alert-popup-row-value'>${props.active ? 'Active' : 'Contained'}</div></div>
                <div class='alert-popup-row'><div class='alert-popup-row-name'>Containment:</div><div class='alert-popup-row-value'>${props.perContained != null ? props.perContained + '%' : 'Not Available'}</div></div>
                ${props.updatedTimestamp ? `<div class='alert-popup-row'><div class='alert-popup-row-name'>Last Updated:</div><div class='alert-popup-row-value'>${formatDate(props.updatedTimestamp)}</div></div>` : ''}
                <div class='alert-popup-row'><div class='alert-popup-row-name'>Reported:</div><div class='alert-popup-row-value'>${props.startTimestamp ? formatDate(props.startTimestamp) : 'Not Available'}</div></div>
                <div class='alert-popup-row'><div class='alert-popup-row-name'>Coordinates:</div><div class='alert-popup-row-value'>${loc[1].toFixed(5)}, ${loc[0].toFixed(5)}</div></div>
                <div class='alert-popup-row-button js-view-incident'>View Incident</div>
              </div>
            `)
            .addTo(map.current);

          const btn = popup.getElement().querySelector('.js-view-incident');
          btn.style.cursor = 'pointer';
          btn.addEventListener('click', () => {
            console.log('clicked');
            setPanel({ name: 'Fire PopUp', content: props });
            popup.remove();
          });

          map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'Layer-Fire-symbol', () => map.current.getCanvas().style.cursor = '');
        eventListenersAdded.current = true;
      }
      console.log('plotFires: Successfully plotted', fires.length, 'fires');
    } catch (error) {
      console.error('plotFires: Error plotting fires:', error);
      console.error('Error stack:', error.stack);
    }
  }, [formatDate, getFireIcon, setPanel]);

  const getFires = useCallback((map, setRefreshing) => {
    console.log('getFires called with map:', map);
    console.log('Map current exists:', !!map?.current);
    if (map?.current) {
      console.log('Map loaded:', map.current.loaded());
      console.log('Style loaded:', map.current.isStyleLoaded());
    }
    
    setRefreshing?.(true);
    let incidents = [];
    axios.get('https://firewatch-api.space-eyes.com/worldwide-incidents')
      .then(res => {
        incidents = res.data.incidents.map(incident => ({
          active: 'Not Available', areaAC: null, cause: 'Undetermined', data_source: 'RSOE-EDIS',
          fire_id: incident.detail_url, fuels: null, geom: null, geom_source: null, last_containment: null,
          latitude: incident.latitude, longitude: incident.longitude,
          name: incident.category || incident.title || 'Name not Available', perContained: null,
          startTimestamp: incident.event_date_utc, updatedTimestamp: incident.last_update_utc,
          location: incident.location, category: incident.category, description: incident.description
        }));
      })
      .catch(err => {
        console.error('Error fetching worldwide incidents:', err);
      })
      .finally(() => {
        axios.get('https://firewatch-api.space-eyes.com/fires?area_id=1751')
          .then(res => {
            const all = [...res.data.fires, ...incidents];
            console.log('Fires fetched:', all.length, 'fires');
            console.log('Map object exists:', !!map?.current);
            if (map?.current) {
              console.log('About to plot fires. Map loaded:', map.current.loaded(), 'Style loaded:', map.current.isStyleLoaded());
            }
            setFires(all);
            plotFires(map, all);
          })
          .catch(err => {
            console.error('error fetching fires:', err);
            console.error('Error details:', err.message, err.response?.data);
          })
          .finally(() => setRefreshing?.(false));
      });
  }, [plotFires]);

  // Function to update a single fire's geometry and replot
  const updateFireGeometry = useCallback((fireId, geometry) => {
    setFires(prevFires => {
      const updatedFires = prevFires.map(fire => 
        fire.fire_id === fireId 
          ? { ...fire, geom: geometry }
          : fire
      );
      return updatedFires;
    });
  }, []);

  const value = useMemo(() => ({ fires, setFires, getFires, plotFires, updateFireGeometry, modal, setModal }), [fires, modal]);
  return <FiresContext.Provider value={value}>{children}</FiresContext.Provider>;
};

export const useFiresContext = () => useContext(FiresContext);
